'use client';

import { usePathname, useSearchParams } from 'next/navigation';
import { useEffect } from 'react';
import { useSessionStorage } from 'react-use';

import { useAbTastyTests } from '@/hooks/use-ab-tasty-tests';
import { useFeatureToggle } from '@/hooks/use-feature-toggle';
import { handleAbTastyImpression } from '@/lib/ab-tasty';
import { pushToDataLayer } from '@/lib/gtm';

type Props = {
  gtmPageType: string;
};

// This is a component and not a hook so it can be used by server components.
// When using, wrap in Suspense so it doesn't cause client-side rendering.
//   <Suspense fallback={null}>
//     <PageLoadEvents gtmPageType="blah" />
//   </Suspense>
// See https://nextjs.org/docs/app/api-reference/functions/use-search-params#behavior
export const PageLoadEvents = ({ gtmPageType: pageType }: Props): null => {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const { isEnabled } = useFeatureToggle();

  const [, setPreviousPageType] = useSessionStorage('pageTypePrevious');
  const [nextPageType, setNextPageType] = useSessionStorage('pageTypeNext');

  useEffect(() => {
    setPreviousPageType(nextPageType);
    setNextPageType(pageType);
    // Using pushToDataLayer because we don't have all the fields of GtmEvent
    pushToDataLayer({ event: 'pageView', pageType });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, searchParams]);
  // listen for further URL changes in case they switch regions/language

  if (isEnabled('ab-tasty')) {
    // eslint-disable-next-line react-hooks/rules-of-hooks -- Condition is project-wide
    const abTastyTests = useAbTastyTests();

    // eslint-disable-next-line react-hooks/rules-of-hooks
    useEffect(() => {
      handleAbTastyImpression(pathname, abTastyTests);
      // We don't want pathname changes to trigger extra impressions
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [abTastyTests]);
  }

  return null;
};
